import { useEffect, useState, useCallback } from "react";
import { Navigate } from "react-router-dom";
import { api, downloadBlob } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { ShieldCheck, ShieldAlert, FileCheck2, KeyRound, Download, ChevronDown, ChevronRight, Search, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import RetentionSettings from "@/components/RetentionSettings";
import FeedbackQueue from "@/components/FeedbackQueue";

const SEV = {
  high: "bg-rose-100 text-rose-800 border-rose-200",
  warning: "bg-[#FDECD0] text-[#7C3F00] border-[#F5A623]/50",
  info: "bg-slate-100 text-slate-700 border-slate-200",
};
const RANGES = [["1", "Last 24 hours"], ["7", "Last 7 days"], ["30", "Last 30 days"], ["365", "Last year"], ["all", "All time"]];

function Stat({ icon: Icon, label, value, sub, tid, tone = "navy" }) {
  const tones = { navy: "bg-[#0A2A5E] text-white", rose: "bg-rose-50 text-rose-800 border border-rose-200", amber: "bg-[#FFF7E8] text-[#7C3F00] border border-[#F5A623]", green: "bg-emerald-50 text-emerald-800 border border-emerald-200" };
  return (
    <Card data-testid={tid} className={`p-4 border-0 ${tones[tone]}`}>
      <div className="flex items-center gap-2 text-xs uppercase tracking-widest font-bold opacity-80"><Icon className="w-4 h-4" /> {label}</div>
      <div className="font-display text-3xl font-extrabold mt-2">{value}</div>
      {sub && <div className="text-xs opacity-80 mt-0.5">{sub}</div>}
    </Card>
  );
}

function Row({ ev }) {
  const [open, setOpen] = useState(false);
  const d = ev.detail || {};
  const headline = d.question || d.title || d.subject || d.month || d.email || d.terms_version || d.template_id || "";
  return (
    <>
      <tr data-testid={`audit-row-${ev.id}`} onClick={() => setOpen(!open)} className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer">
        <td className="px-3 py-2.5 text-slate-500 whitespace-nowrap text-xs">{new Date(ev.created_at).toLocaleString()}</td>
        <td className="px-3 py-2.5"><span className={`text-[10px] px-1.5 py-0.5 rounded border font-bold uppercase ${SEV[ev.severity] || SEV.info}`}>{ev.severity}</span></td>
        <td className="px-3 py-2.5 font-mono text-xs text-[#0A2A5E]">{ev.event_type}</td>
        <td className="px-3 py-2.5 text-slate-700 text-xs">{ev.user_email || "—"}{ev.user_role && <span className="text-slate-400"> · {ev.user_role}</span>}</td>
        <td className="px-3 py-2.5 text-slate-700 text-xs max-w-md truncate">{headline}</td>
        <td className="px-2 py-2.5 text-slate-400">{open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}</td>
      </tr>
      {open && (
        <tr className="bg-[#F5F9FE]">
          <td colSpan={6} className="px-4 py-3">
            <pre data-testid="audit-detail" className="text-xs text-slate-700 whitespace-pre-wrap font-mono">{JSON.stringify(d, null, 2)}</pre>
          </td>
        </tr>
      )}
    </>
  );
}

export default function Audit() {
  const { user } = useAuth();
  const [summary, setSummary] = useState(null);
  const [items, setItems] = useState([]);
  const [canExport, setCanExport] = useState(true);
  const [types, setTypes] = useState([]);
  const [type, setType] = useState("all");
  const [sev, setSev] = useState("all");
  const [range, setRange] = useState("30");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);

  const params = useCallback(() => {
    const p = new URLSearchParams();
    if (type !== "all") p.set("event_type", type);
    if (sev !== "all") p.set("severity", sev);
    if (q.trim()) p.set("q", q.trim());
    if (range !== "all") p.set("since", new Date(Date.now() - Number(range) * 86400000).toISOString());
    return p;
  }, [type, sev, q, range]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, l] = await Promise.all([api.get("/audit/summary"), api.get(`/audit?${params().toString()}`)]);
      setSummary(s.data); setItems(l.data.items); setTypes(l.data.types); setCanExport(l.data.can_export !== false);
    } catch { toast.error("Could not load audit trail"); }
    finally { setLoading(false); }
  }, [params]);

  useEffect(() => { if (user?.role === "manager") load(); }, [load, user]);

  const exportCsv = async () => {
    try {
      await downloadBlob(`/audit/export.csv?${params().toString()}`, `lumis-hr-audit-${new Date().toISOString().slice(0, 10)}.csv`);
      toast.success("Audit CSV downloaded");
      load();
    } catch { toast.error("Export failed"); }
  };

  if (user?.role !== "manager") return <Navigate to="/app/chat" replace />;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-[#C7761A] font-bold">Governance</div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold text-[#0A2A5E]">Compliance Audit Trail</h1>
          <p className="text-sm text-slate-600 mt-1">Timestamped audit record of terms acceptances, high-risk AI conversations, document changes, KPI edits, and sensitive form activity.</p>
        </div>
        <Button data-testid="audit-export-btn" onClick={exportCsv} disabled={!canExport} title={canExport ? undefined : "Audit export is restricted to the workspace owner"} className="bg-[#0A2A5E] hover:bg-[#0F3F8A] text-white"><Download className="w-4 h-4 mr-2" /> Export CSV</Button>
      </div>

      {summary && (
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
          <Stat tid="stat-terms" icon={ShieldCheck} tone="green" label="Terms coverage" value={`${summary.terms_coverage.accepted}/${summary.terms_coverage.total}`} sub={`users on v${summary.terms_coverage.version} · ${summary.terms_acceptances} acceptances in 30d`} />
          <Stat tid="stat-high-risk" icon={ShieldAlert} tone="rose" label="High-risk manager matters" value={summary.high_risk_chats} sub="flagged for human review · 30d" />
          <Stat tid="stat-forms" icon={FileCheck2} tone="amber" label="Form submissions" value={summary.sensitive_forms} sub="reviews, scoresheets, plans · 30d" />
          <Stat tid="stat-logins" icon={KeyRound} label="Failed logins" value={summary.failed_logins} sub={`${summary.total_events} total events · 30d`} />
        </div>
      )}

      <FeedbackQueue />

      <RetentionSettings onChanged={load} />

      <Card className="p-4 bg-white border-slate-200 mb-4">
        <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3">
          <div className="relative lg:col-span-2">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input data-testid="audit-search" placeholder="Search email, question, title…" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} className="pl-9" />
          </div>
          <Select value={type} onValueChange={setType}>
            <SelectTrigger data-testid="audit-type-filter"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">All event types</SelectItem>{types.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={sev} onValueChange={setSev}>
            <SelectTrigger data-testid="audit-severity-filter"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">All severities</SelectItem><SelectItem value="high">High</SelectItem><SelectItem value="warning">Warning</SelectItem><SelectItem value="info">Info</SelectItem></SelectContent>
          </Select>
          <Select value={range} onValueChange={setRange}>
            <SelectTrigger data-testid="audit-range-filter"><SelectValue /></SelectTrigger>
            <SelectContent>{RANGES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </Card>

      <Card className="bg-white border-slate-200 overflow-hidden">
        <div className="flex items-center justify-between px-4 py-2 bg-slate-50 text-xs text-slate-500">
          <span data-testid="audit-count">{items.length} event{items.length === 1 ? "" : "s"}{items.length >= 200 && " (showing latest 200 — narrow filters or export CSV)"}</span>
          <Button size="sm" variant="ghost" data-testid="audit-refresh" onClick={load} disabled={loading}><RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /></Button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
              <tr><th className="px-3 py-2">Timestamp</th><th className="px-3 py-2">Severity</th><th className="px-3 py-2">Event</th><th className="px-3 py-2">Actor</th><th className="px-3 py-2">Summary</th><th /></tr>
            </thead>
            <tbody>
              {items.map((ev) => <Row key={ev.id} ev={ev} />)}
              {items.length === 0 && <tr><td colSpan={6} className="px-4 py-10 text-center text-slate-500">No events match these filters.</td></tr>}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
